import { apiSlice } from '../apiSlice';
import { MESSAGES } from './scheduledFlowsMessages';
import { API_TAGS } from '../../constants/api';
import { API_SCHEDULER_URL } from '../../constants/env';

export const scheduledFlowsSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getScheduledFlows: builder.query({
      query: ({ orgId, tenantId, urlParams = {} }) => ({
        url: `api/org/${orgId}/tenant/${tenantId}/scheduled-flows`,
        API_URL: API_SCHEDULER_URL,
        method: 'GET',
        params: urlParams,
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.FETCH_SCHEDULED_FLOWS.ERROR
        }
      }),
      providesTags: (result, error) => {
        if (error?.error) {
          return [{ type: API_TAGS.SCHEDULED_FLOWS, id: 'LIST' }];
        }
        return [
          ...(result?.items || []).map(({ id }) => ({ type: API_TAGS.SCHEDULED_FLOWS, id })),
          { type: API_TAGS.SCHEDULED_FLOWS, id: 'LIST' }
        ];
      }
    }),

    createScheduledFlow: builder.mutation({
      query: ({ orgId, tenantId, ...data }) => ({
        url: `api/org/${orgId}/tenant/${tenantId}/scheduled-flows`,
        API_URL: API_SCHEDULER_URL,
        method: 'POST',
        body: data,
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.CREATE_SCHEDULED_FLOW.ERROR
        },
        successMessage: {
          showMessage: true,
          messages: MESSAGES.CREATE_SCHEDULED_FLOW.SUCCESS
        }
      }),
      transformResponse: (response) => true,
      invalidatesTags: (result, error) => {
        if (error) {
          return;
        }
        return [{ type: API_TAGS.SCHEDULED_FLOWS, id: 'LIST' }];
      }
    }),

    updateScheduledFlow: builder.mutation({
      query: ({ id, orgId, tenantId, ...data }) => ({
        url: `api/org/${orgId}/tenant/${tenantId}/scheduled-flows/${id}`,
        API_URL: API_SCHEDULER_URL,
        method: 'PUT',
        body: data,
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.UPDATE_SCHEDULED_FLOW.ERROR
        },
        successMessage: {
          showMessage: true,
          messages: MESSAGES.UPDATE_SCHEDULED_FLOW.SUCCESS
        }
      }),
      transformResponse: (response) => true,
      invalidatesTags: (result, error) => {
        if (error) {
          return;
        }
        return [{ type: API_TAGS.SCHEDULED_FLOWS, id: 'LIST' }];
      }
    }),

    activateScheduledFlow: builder.mutation({
      query: ({ id, orgId, tenantId }) => ({
        url: `api/org/${orgId}/tenant/${tenantId}/scheduled-flows/${id}/activate`,
        API_URL: API_SCHEDULER_URL,
        method: 'PUT',
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.ACTIVATE_SCHEDULED_FLOW.ERROR
        },
        successMessage: {
          showMessage: true,
          messages: MESSAGES.ACTIVATE_SCHEDULED_FLOW.SUCCESS
        }
      }),
      transformResponse: (response) => true,
      invalidatesTags: (result, error) => {
        if (error) {
          return;
        }
        return [{ type: API_TAGS.SCHEDULED_FLOWS, id: 'LIST' }];
      }
    }),

    deactivateScheduledFlow: builder.mutation({
      query: ({ id, orgId, tenantId }) => ({
        url: `api/org/${orgId}/tenant/${tenantId}/scheduled-flows/${id}/deactivate`,
        API_URL: API_SCHEDULER_URL,
        method: 'PUT',
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.DEACTIVATE_SCHEDULED_FLOW.ERROR
        },
        successMessage: {
          showMessage: true,
          messages: MESSAGES.DEACTIVATE_SCHEDULED_FLOW.SUCCESS
        }
      }),
      transformResponse: (response) => true,
      invalidatesTags: (result, error) => {
        if (error) {
          return;
        }
        return [{ type: API_TAGS.SCHEDULED_FLOWS, id: 'LIST' }];
      }
    }),

    deleteScheduledFlow: builder.mutation({
      query: ({ id, orgId, tenantId }) => ({
        url: `api/org/${orgId}/tenant/${tenantId}/scheduled-flows/${id}`,
        API_URL: API_SCHEDULER_URL,
        method: 'DELETE',
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.DELETE_SCHEDULED_FLOW.ERROR
        },
        successMessage: {
          showMessage: true,
          messages: MESSAGES.DELETE_SCHEDULED_FLOW.SUCCESS
        }
      }),
      transformResponse: (response) => true,
      invalidatesTags: (result, error) => {
        if (error) {
          return;
        }
        return [{ type: API_TAGS.SCHEDULED_FLOWS, id: 'LIST' }];
      }
    }),

    getScheduledFlowExecutions: builder.query({
      query: ({ id, orgId, tenantId, page = 0, pageSize = 10 }) => ({
        url: `api/org/${orgId}/tenant/${tenantId}/scheduled-flows/${id}/executions`,
        API_URL: API_SCHEDULER_URL,
        method: 'GET',
        params: { page, pageSize },
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.FETCH_SCHEDULED_FLOW_EXECUTIONS.ERROR
        }
      }),
      serializeQueryArgs: ({ queryArgs }) => {
        return `scheduledFlowExecutions-${queryArgs.id}`;
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        return currentArg?.page !== previousArg?.page;
      },
      merge: (currentCache, newData, { arg }) => {
        if (arg?.page === 0) {
          currentCache.items = [...newData.items];
        } else {
          const existingIds = new Set(currentCache.items.map(item => item.id));
          const newItems = newData.items.filter(item => !existingIds.has(item.id));
          currentCache.items.push(...newItems);
        }
        currentCache.total = newData.total;
      },
      providesTags: (result, error, args) => [
        { type: API_TAGS.SCHEDULED_FLOW_EXECUTIONS, id: args.id }
      ]
    }),

    triggerScheduledFlowExecution: builder.mutation({
      query: ({ id, orgId, tenantId }) => ({
        url: `api/org/${orgId}/tenant/${tenantId}/scheduled-flows/${id}/execute`,
        API_URL: API_SCHEDULER_URL,
        method: 'POST',
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.TRIGGER_SCHEDULED_FLOW_EXECUTION.ERROR
        },
        successMessage: {
          showMessage: true,
          messages: MESSAGES.TRIGGER_SCHEDULED_FLOW_EXECUTION.SUCCESS
        }
      }),
      transformResponse: (response) => true,
      invalidatesTags: (result, error, args) => {
        if (error) {
          return;
        }
        return [
          { type: API_TAGS.SCHEDULED_FLOWS, id: 'LIST' },
          { type: API_TAGS.SCHEDULED_FLOW_EXECUTIONS, id: args.id }
        ];
      }
    })
  })
});

export const {
  useGetScheduledFlowsQuery,
  useCreateScheduledFlowMutation,
  useUpdateScheduledFlowMutation,
  useActivateScheduledFlowMutation,
  useDeactivateScheduledFlowMutation,
  useDeleteScheduledFlowMutation,
  useGetScheduledFlowExecutionsQuery,
  useTriggerScheduledFlowExecutionMutation
} = scheduledFlowsSlice;
